import { Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout, AuthLayout } from '@/app/AppLayout';
import { RequireAuth, RequireCapability } from '@/app/guards';
import { LoginPage } from '@/features/auth/LoginPage';
import { ScaPage } from '@/features/auth/ScaPage';
import { SelectCompanyPage } from '@/features/auth/SelectCompanyPage';
import { HomePage } from '@/features/home/HomePage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { AccountDetailPage } from '@/features/accounts/AccountDetailPage';
import { TransactionsPage } from '@/features/transactions/TransactionsPage';
import { TransactionDetailPage } from '@/features/transactions/TransactionDetailPage';
import { BeneficiariesPage } from '@/features/beneficiaries/BeneficiariesPage';
import { PaymentsPage } from '@/features/payments/PaymentsPage';
import { NewPaymentPage } from '@/features/payments/NewPaymentPage';
import { PaymentDetailPage } from '@/features/payments/PaymentDetailPage';
import { ApprovalsPage } from '@/features/approvals/ApprovalsPage';
import { ApprovalDetailPage } from '@/features/approvals/ApprovalDetailPage';
import { CardsPage } from '@/features/cards/CardsPage';
import { CardDetailPage } from '@/features/cards/CardDetailPage';
import { MorePage } from '@/features/more/MorePage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { ActivityPage } from '@/features/activity/ActivityPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { SecurityPage } from '@/features/security/SecurityPage';
import { ContinueOnWebPage } from '@/features/handoff/ContinueOnWebPage';
import { PersonalisationPage } from '@/features/personalisation/PersonalisationPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { FxPage } from '@/features/fx/FxPage';
import { InsightsPage } from '@/features/insights/InsightsPage';
import {
  CompanyProfilePage,
  ConnectedAccountsPage,
  DocumentsPage,
  ExpensesPage,
  InvoicesPage,
  OnboardingStatusPage,
  TaxPage,
  TeamPage,
  WalletsSpacesPage,
} from '@/features/misc/SimplePages';
import { DemoIndexPage } from '@/demo/DemoIndexPage';
import { NotFoundPage } from '@/app/NotFoundPage';

/**
 * Route table.
 *
 * Canonical route set reconciled from the traceability corpus and
 * baseline_handoff/app/03_INFORMATION_ARCHITECTURE_AND_ROUTES.md. Alias paths
 * that appear in the corpus redirect to the canonical path so there is exactly
 * one destination per feature (TT-R145-01).
 */
export const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/demo-index" replace /> },

  // Desktop demonstration surfaces. Deliberately outside the phone shell.
  { path: '/demo-index', element: <DemoIndexPage /> },
  // The app frames itself on desktop with live controls beside it, so the old
  // iframe-based device page is gone. The route is kept so existing links work.
  { path: '/mobile-demo', element: <Navigate to="/mobile/home" replace /> },

  {
    element: <AuthLayout />,
    children: [
      { path: '/mobile/login', element: <LoginPage /> },
      { path: '/mobile/sca', element: <ScaPage /> },
      { path: '/mobile/select-company', element: <SelectCompanyPage /> },
    ],
  },

  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/mobile/home', element: <HomePage /> },
      { path: '/mobile/more', element: <MorePage /> },

      {
        path: '/mobile/accounts',
        element: (
          <RequireCapability capability="accountsView" title="Accounts">
            <AccountsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/accounts/:accountId',
        element: (
          <RequireCapability capability="accountsView" title="Account">
            <AccountDetailPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/transactions',
        element: (
          <RequireCapability capability="transactionsView" title="Transactions">
            <TransactionsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/transactions/:transactionId',
        element: (
          <RequireCapability capability="transactionsView" title="Transaction">
            <TransactionDetailPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/beneficiaries',
        element: (
          <RequireCapability capability="beneficiariesView" title="Beneficiaries">
            <BeneficiariesPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/payments',
        element: (
          <RequireCapability capability="paymentsView" title="Payments">
            <PaymentsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/payments/new',
        element: (
          <RequireCapability capability="paymentsCreate" title="Send money">
            <NewPaymentPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/payments/:paymentId',
        element: (
          <RequireCapability capability="paymentsView" title="Payment">
            <PaymentDetailPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/approvals',
        element: (
          <RequireCapability capability="approvalsView" title="Approvals">
            <ApprovalsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/approvals/:approvalId',
        element: (
          <RequireCapability capability="approvalsView" title="Approval">
            <ApprovalDetailPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/cards',
        element: (
          <RequireCapability capability="cardsView" title="Cards">
            <CardsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/cards/:cardId',
        element: (
          <RequireCapability capability="cardsView" title="Card">
            <CardDetailPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/notifications',
        element: (
          <RequireCapability capability="notificationsView" title="Notifications">
            <NotificationsPage />
          </RequireCapability>
        ),
      },
      { path: '/mobile/activity', element: <ActivityPage /> },
      {
        path: '/mobile/settings/personalisation',
        element: (
          <RequireCapability capability="personalisation" title="Edit home">
            <PersonalisationPage />
          </RequireCapability>
        ),
      },

      {
        path: '/mobile/reports',
        element: (
          <RequireCapability capability="reportsView" title="Reports">
            <ReportsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/fx',
        element: (
          <RequireCapability capability="fxView" title="Exchange">
            <FxPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/insights',
        element: (
          <RequireCapability capability="aiSummary" title="Insights">
            <InsightsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/team',
        element: (
          <RequireCapability capability="teamView" title="Team">
            <TeamPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/documents',
        element: (
          <RequireCapability capability="documentsView" title="Documents">
            <DocumentsPage />
          </RequireCapability>
        ),
      },
      { path: '/mobile/onboarding-status', element: <OnboardingStatusPage /> },
      {
        path: '/mobile/invoices',
        element: (
          <RequireCapability capability="reportsView" title="Invoices">
            <InvoicesPage />
          </RequireCapability>
        ),
      },
      { path: '/mobile/company-profile', element: <CompanyProfilePage /> },
      { path: '/mobile/onboarding', element: <Navigate to="/mobile/onboarding-status" replace /> },
      {
        path: '/mobile/tax',
        element: (
          <RequireCapability capability="taxView" title="Tax calendar">
            <TaxPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/connected-accounts',
        element: (
          <RequireCapability capability="accountingView" title="Connected accounting">
            <ConnectedAccountsPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/expenses',
        element: (
          <RequireCapability capability="expenseEntry" title="Expenses & receipts">
            <ExpensesPage />
          </RequireCapability>
        ),
      },
      {
        path: '/mobile/wallets-spaces',
        element: (
          <RequireCapability capability="digitalAssetsView" title="Wallets and spaces">
            <WalletsSpacesPage />
          </RequireCapability>
        ),
      },
      { path: '/mobile/profile', element: <ProfilePage /> },
      { path: '/mobile/security', element: <SecurityPage /> },
      { path: '/mobile/continue-on-web', element: <ContinueOnWebPage /> },
    ],
  },

  // Canonical-path aliases seen in the traceability corpus.
  { path: '/mobile/exchange', element: <Navigate to="/mobile/fx" replace /> },

  { path: '*', element: <NotFoundPage /> },
];
